import * as React from "react";
import { Button as PrimeButton } from "primereact/button";
import { cn } from "@/lib/utils";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", asChild = false, type = "button", children, ...props }, ref) => {
    // Mapear variantes para classes PrimeReact
    const getPrimeSeverity = (): "success" | "info" | "warning" | "danger" | "secondary" | null => {
      switch (variant) {
        case "destructive":
          return "danger";
        case "secondary":
          return "secondary";
        default:
          return null;
      }
    };

    const getPrimeVariant = (): "text" | "outlined" | null => {
      switch (variant) {
        case "outline":
          return "outlined";
        case "ghost":
        case "link":
          return "text";
        default:
          return null;
      }
    };

    // Mapear tamanhos
    const getPrimeSize = (): "small" | "large" | null => {
      switch (size) {
        case "sm":
          return "small";
        case "lg":
          return "large";
        default:
          return null;
      }
    };

    const severity = getPrimeSeverity();
    const outlined = variant === "outline";
    const text = variant === "ghost" || variant === "link";
    const primeSize = getPrimeSize();

    // Classes customizadas para manter visual atual
    const customClasses = cn(
      // Tamanho icon
      size === "icon" && "p-button-icon-only",
      // Link variant precisa de estilo especial
      variant === "link" && "p-button-link",
      className
    );

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...props,
        ref,
        className: cn((children.props as { className?: string }).className, customClasses),
      } as React.HTMLAttributes<HTMLElement> & { ref: React.Ref<HTMLElement> });
    }

    return (
      <PrimeButton
        type={type}
        label={typeof children === "string" ? children : undefined}
        severity={severity ?? undefined}
        outlined={outlined}
        text={text}
        size={primeSize ?? undefined}
        className={customClasses}
        {...props}
      >
        {typeof children !== "string" ? children : undefined}
      </PrimeButton>
    );
  }
);
Button.displayName = "Button";

export { Button };
